/**
 * A point on the map, and the little arithmetic the location picker needs.
 *
 * Kept apart from the map itself so it can be tested without Leaflet, and so a screen that only
 * shows coordinates never pulls the map into its bundle.
 */

/** WGS 84 degrees: what OpenStreetMap, a phone's map application and the API all speak. */
export interface GeoPoint {
  readonly latitude: number;
  readonly longitude: number;
}

/**
 * Where an empty map opens: central Cairo, at a zoom that shows Greater Cairo and Giza. Most
 * clinics are there, and one that is not is a drag and a zoom away.
 */
export const DEFAULT_CENTRE: GeoPoint = { latitude: 30.0444, longitude: 31.2357 };
export const DEFAULT_ZOOM = 11;

/** Street level, where a pin can be told apart from the building next door. */
export const PIN_ZOOM = 17;

/**
 * Six decimal places is about 11 cm. A mouse click is not that precise, so anything finer is noise
 * that would only make the coordinates harder to read back to someone over the phone.
 */
export function roundPoint(point: GeoPoint): GeoPoint {
  return { latitude: round6(point.latitude), longitude: round6(point.longitude) };
}

/** `30.007413, 31.491318`: the order and form Google Maps copies when you right-click a place. */
export function formatPoint(point: GeoPoint): string {
  return `${round6(point.latitude)}, ${round6(point.longitude)}`;
}

/**
 * Reads "latitude, longitude" as typed or pasted. Accepts a comma, a space or both between the
 * two, and Arabic-Indic digits, since an Arabic keyboard types ٣٠٫٠٤ for 30.04. Returns null for
 * anything that is not exactly two numbers, or that lies off the globe.
 */
export function parsePoint(text: string): GeoPoint | null {
  const western = text
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/٫/g, '.')
    .replace(/[،؛]/g, ',');
  const match = /^\s*(-?\d+(?:\.\d+)?)\s*(?:,\s*|\s+)(-?\d+(?:\.\d+)?)\s*$/.exec(western);
  if (!match) {
    return null;
  }
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (!isOnTheGlobe(latitude, longitude)) {
    return null;
  }
  return roundPoint({ latitude, longitude });
}

export function isOnTheGlobe(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180
  );
}

/** The branch's own pin, if it has one. The API sends both coordinates or neither. */
export function pointOf(place: {
  readonly latitude?: number | null;
  readonly longitude?: number | null;
}): GeoPoint | null {
  if (place.latitude == null || place.longitude == null) {
    return null;
  }
  return { latitude: place.latitude, longitude: place.longitude };
}

export function samePoint(a: GeoPoint | null, b: GeoPoint | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return a.latitude === b.latitude && a.longitude === b.longitude;
}

/** A link that opens the point on openstreetmap.org, for checking a pin against the street. */
export function openStreetMapLink(point: GeoPoint): string {
  const { latitude, longitude } = point;
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=${PIN_ZOOM}/${latitude}/${longitude}`;
}

function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
